import { getMaterialType, MaterialTypeEnum } from "@/stores/clip/data/constant";
import { TrackConfig } from "@/stores/clip/data/trackConfig";
import trackStateStore from "@/stores/clip/trackState";
import { getGridPixel } from "@/utils/canvasUtil";
import { Message } from "@arco-design/web-react";
import { useEffect, useRef, useState } from "react";

export interface useListenerProps {
  onDragEnd?: (data: {
    sourceData: any;
    position?: {
      x: number;
      y: number;
    };
    line: number | null;
    index: number | null;
  }) => void;
}

const useDragListener = (props: useListenerProps) => {
  const { onDragEnd } = props;
  const source = useRef(null);
  const targetTemp = useRef(null);
  const cloneElement = useRef(null);
  const sourceData = useRef(null); // 拖拽元素上绑定的数据
  const isValid = useRef(false);
  const dragPosition = useRef({
    x: 0,
    y: 0,
  });

  const createCloneElement = (e) => {
    cloneElement.current = source.current.cloneNode(true);
    cloneElement.current.style.position = "fixed";
    cloneElement.current.style.zIndex = 999;
    cloneElement.current.style.left = e.clientX + "px";
    cloneElement.current.style.top = e.clientY + "px";
    cloneElement.current.style.opacity = "0.8";
    cloneElement.current.style.scale = "0.85";
    source.current.parentElement.appendChild(cloneElement.current);
  };

  const createTrack = (options: any) => {
    if (!cloneElement.current) return;
    const materialType = getMaterialType(sourceData.current?.type);
    if (!materialType) {
      console.error("data-source 传递过来的type错误");
    }

    const config = TrackConfig.get(materialType);
    cloneElement.current.style.left = options.x + "px";
    cloneElement.current.style.top = options.y + "px";
    cloneElement.current.style.width =
      getGridPixel(trackStateStore.trackScale, 30 * 5) + "px";
    cloneElement.current.style.height = "40px";
    cloneElement.current.style.fontSize = "14px";
    cloneElement.current.style.borderRadius = "6px";
    cloneElement.current.style.color = config.color || "#fff";
    cloneElement.current.style.textAlign = "center";
    cloneElement.current.style.lineHeight = "40px";
    cloneElement.current.style.background = config.background;
  };

  const handleMouseDown = (e: MouseEvent) => {
    isValid.current = false;
    source.current = (e.target as HTMLElement).closest(
      ".drag-source"
    ) as HTMLElement;

    if (source.current) {
      // 阻止图片默认的拖动行为
      e.preventDefault();

      const data = source.current.dataset.data as any;
      if (!(e.target as HTMLElement).classList.contains("arco-checkbox-mask")) {
        createCloneElement(e);
      }

      if (data) {
        sourceData.current = JSON.parse(data);
      } else {
        sourceData.current = null;
      }
    }
  };

  const handleMouseMove = (e) => {
    if (e.target === document || !source.current || !sourceData.current) {
      return;
    }

    if (cloneElement.current) {
      cloneElement.current.style.left = e.clientX + "px";
      cloneElement.current.style.top = e.clientY + "px";
    }

    const targetGroup = (e.target as HTMLElement)?.closest(
      ".trackItem"
    ) as HTMLElement;

    const targetTrack = (e.target as HTMLElement)?.closest(
      ".trackLine"
    ) as HTMLElement;

    const isInTrackList = (e.target as HTMLElement)?.closest(
      ".track-list"
    ) as HTMLElement;

    if (isInTrackList) {
      createTrack({ x: e.clientX, y: e.clientY });
    }

    if (targetGroup) {
      const lineIndex = targetGroup.dataset.line;
      const trackType = trackStateStore.trackList[lineIndex]?.type;
      const sourceType = sourceData.current?.type;

      // 素材类型和轨道类型不一样
      if (MaterialTypeEnum[trackType] !== sourceType) {
        // 非视频轨道直接error
        if (trackType !== "video") {
          isValid.current = false;
          targetGroup.classList?.add("drag-error");
        } else {
          // 视频轨道可以放图片和视频
          if (
            [MaterialTypeEnum["image"], MaterialTypeEnum["video"]].includes(
              sourceType
            )
          ) {
            isValid.current = true;
            targetGroup.classList?.add("drag-over");
          } else {
            isValid.current = false;
            targetGroup.classList?.add("drag-error");
          }
        }
      } else {
        isValid.current = true;
        targetGroup.classList?.add("drag-over");
      }

      targetTemp.current = targetGroup;

      targetGroup.addEventListener(
        "mouseleave",
        () => {
          if (targetTemp.current) {
            targetTemp.current.classList.remove(
              isValid.current ? "drag-over" : "drag-error"
            );
          }
        },
        { once: true }
      );
    } else {
      isValid.current = true;
    }
  };

  const handleMouseUp = (e) => {
    dragPosition.current = {
      x: e.clientX,
      y: e.clientY,
    };

    trackStateStore.setDragMaterialData({
      dragPoint: {
        x: e.clientX,
        y: e.clientY,
      },
    });

    if (e.target === document || !source.current) {
      return;
    }
    if (cloneElement.current) {
      source.current.parentElement?.removeChild(cloneElement.current);
      cloneElement.current = null;
    }
    const target = (e.target as HTMLElement)?.closest(
      ".trackItem"
    ) as HTMLElement;

    const trackLine = (e.target as HTMLElement)?.closest(
      ".trackLine"
    ) as HTMLElement;

    const isInTrackList = (e.target as HTMLElement)?.closest(
      ".track-list"
    ) as HTMLElement;

    // console.log("handleMouseUp", {
    //   target,
    //   trackLine,
    //   isInTrackList,
    //   isValid: isValid.current,
    // });

    if (!trackLine) {
      if (isInTrackList) {
        onDragEnd?.({
          sourceData: sourceData.current,
          position: dragPosition.current,
          line: null,
          index: null,
        });
        isValid.current = true;
        return;
      }

      isValid.current = false;
      return;
    }

    if (!target && trackLine && isValid.current) {
      onDragEnd?.({
        sourceData: sourceData.current,
        position: dragPosition.current,
        line: Number(trackLine.dataset.index),
        index: null,
      });
      isValid.current = false;
      return;
    }

    if (isValid.current) {
      target.classList.remove("drag-over");

      onDragEnd?.({
        sourceData: sourceData.current,
        position: dragPosition.current,
        line: Number(target.dataset.line),
        index: Number(target.dataset.index),
      });
      isValid.current = false;
    }

    trackStateStore.setDragMaterialData({
      dragPoint: {
        x: 0,
        y: 0,
      },
    });
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  return {
    sourceData,
    dragPosition,
  };
};

export default useDragListener;
